import React, { useEffect, useMemo, useState } from 'react';
import AnimatedProgressRing from '../visual/AnimatedProgressRing';

/**
 * FocusTimer — a working countdown for a focus block or a rest break, shown as a
 * large circular progress ring. Start / pause / resume / reset, selectable length,
 * a smooth per-second sweep and a gentle completion state. Used for the "Focused"
 * mood (work block) and "Tired" (short rest).
 */
export interface FocusTimerProps {
  presets: number[]; // minutes
  label: string;
  doneMessage: string;
}

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

export const FocusTimer: React.FC<FocusTimerProps> = ({ presets, label, doneMessage }) => {
  const [minutes, setMinutes] = useState(presets[0]);
  const [secondsLeft, setSecondsLeft] = useState(presets[0] * 60);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => {
    if (secondsLeft === 0) setRunning(false);
  }, [secondsLeft]);

  const pickPreset = (m: number) => {
    setMinutes(m);
    setSecondsLeft(m * 60);
    setRunning(false);
  };

  const reset = () => {
    setSecondsLeft(minutes * 60);
    setRunning(false);
  };

  const progress = useMemo(() => 1 - secondsLeft / (minutes * 60), [secondsLeft, minutes]);
  const done = secondsLeft === 0;
  const atStart = secondsLeft === minutes * 60;

  return (
    <div className="mm-timer">
      <div className="mm-timer__presets" role="group" aria-label={`${label} length`}>
        {presets.map((m) => (
          <button
            key={m}
            type="button"
            className={`mm-chip ${m === minutes ? 'is-active' : ''}`}
            onClick={() => pickPreset(m)}
          >
            {m}m
          </button>
        ))}
      </div>

      <AnimatedProgressRing
        value={done ? 1 : progress}
        instant={atStart}
        durationMs={1000}
        size={208}
        stroke={12}
        label={done ? doneMessage : `${label}, ${fmt(secondsLeft)} remaining`}
      >
        <div className={`mm-timer__inner ${done ? 'is-done' : ''}`}>
          <span className="mm-timer__time">{done ? '✓' : fmt(secondsLeft)}</span>
          <span className="mm-timer__caption">{done ? doneMessage : label}</span>
        </div>
      </AnimatedProgressRing>

      <div className="mm-activity__controls">
        <button type="button" className="mm-btn mm-btn--primary" onClick={() => setRunning((r) => !r)} disabled={done}>
          {running ? 'Pause' : atStart ? 'Start' : 'Resume'}
        </button>
        <button type="button" className="mm-btn mm-btn--ghost" onClick={reset}>
          Reset
        </button>
      </div>
    </div>
  );
};

export default FocusTimer;
